/**
 * Assistant identity and remaining FE-side prompt fragments for the focused
 * view's AI layer. The assistant name maps to a server-side route; the system
 * prompt and end-of-call summary instruction now live on the BE (CoachAgent
 * starter prompt + CoachPostCallSummaryAction handler).
 *
 * The two BE wire constants are re-exported from `aida/constants/coachingContract`, which the
 * legacy voice-agent panel reads too — same assistant, one definition.
 */

export { LIVE_SUGGESTION_PROMPT_HEADER } from '../../constants/coachingContract';
export { SALES_LIVE_COACHING_ASSISTANT_NAME as COACH_ASSISTANT_NAME } from '../../constants/coachingContract';

/** Sent in place of transcript when the rep asks again with nothing new said, so the coach doesn't repeat itself. */
export const NO_NEW_TRANSCRIPT_RETRY = "I don't like the last suggestion. Give me another one.";

/** Sent instead when there is no earlier suggestion on this call to reject. */
export const NO_TRANSCRIPT_YET = 'Nothing new has been said yet. Give the rep the best next thing to say.';

export type SuggestionSteer = { id: string; label: string; instruction: string };

/** One-shot steers offered beside "Get suggestion" for when the coach gets stuck on one line of advice. */
export const SUGGESTION_STEERS: readonly SuggestionSteer[] = [
	{
		id: 'dont-fold',
		label: "Don't fold",
		instruction:
			"Don't let the rep fold. Stop suggesting the rep give up, end the call, or accept a callback too far out. Give one line that keeps the conversation going or gets a nearer commitment.",
	},
	{
		id: 'alternative',
		label: 'Give me an alternative suggestion',
		instruction: "Give a different suggestion from the last one, and from anything else you've suggested on this call.",
	},
	{
		id: 'industry-stat',
		label: 'Give me an industry stat',
		instruction:
			"Give the rep one industry stat for this prospect's vertical that supports talking to us, such as how much time accountants lose chasing client documents. Only use a stat from your context; never make up a number.",
	},
	{
		id: 'social-proof',
		label: 'Give me social proof',
		instruction:
			'Give the rep one line of social proof for this prospect: a relevant case study, how many customers use the same management system they do, or how many businesses like theirs use Levitate. Only use facts from your context; never make up a number or a customer.',
	},
];
